import { useState } from 'react';
import { api, type UserView } from '../api.ts';
import { GeneratedPassword } from './GeneratedPassword.tsx';
import { PasswordEntry } from './PasswordEntry.tsx';
import { message } from './Platform.tsx';

interface OperatorPasswordProps {
  readonly accountId: string;
  readonly user: UserView;
  /** Called after a successful reset, so the screen can reload. */
  readonly onReset: () => void;
  /**
   * Closes the drawer, which unmounts it and every copy of the password with it.
   * The caller returns focus to whatever opened it.
   */
  readonly onClose: () => void;
}

/**
 * Set a new password for one of a client's operators — the locked-out case,
 * with no mail infrastructure to send a reset link through.
 *
 * Every session the person holds ends, which is not optional: a password is
 * reset because the old one is suspect, and sessions opened with it would
 * otherwise outlive the reset.
 */
export const OperatorPassword = ({ accountId, user, onReset, onClose }: OperatorPasswordProps) => {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [revealed, setRevealed] = useState<string | null>(null);

  const reset = async () => {
    // A double click would post twice and end the sessions the first reset
    // left, and the panel would show whichever password landed last.
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { revokedSessions } = await api.resetOperatorPassword(accountId, user.id, password);
      setRevealed(password);
      setPassword('');
      setAnnouncement(
        `${user.displayName} has a new password, shown once below. ` +
          (revokedSessions === 1
            ? 'Their one open session was ended.'
            : `${revokedSessions} open sessions were ended.`),
      );
      onReset();
    } catch (cause) {
      setError(message(cause));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pf-drawer">
      <p className="sr-only" role="status" aria-live="polite">
        {announcement}
      </p>
      {revealed !== null ? (
        <GeneratedPassword name={user.displayName} password={revealed} onDismiss={onClose} />
      ) : (
        <>
          <p>
            Set a new password for <strong>{user.displayName}</strong>. It replaces the current one,
            which nobody can see, and every session they have open ends.
          </p>
          {error !== null && (
            <p className="error-banner" role="alert">
              {error}
            </p>
          )}
          <PasswordEntry
            id={`pf-password-${user.id}`}
            label="New password"
            value={password}
            disabled={busy}
            onChange={setPassword}
          />
          <div className="toolbar">
            <button
              type="button"
              className="btn btn-primary btn-sm"
              aria-busy={busy || undefined}
              disabled={busy || password === ''}
              onClick={() => void reset()}
            >
              {busy ? 'Setting…' : 'Set this password'}
            </button>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={onClose}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};
